import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  getNotifications,
  markNotificationAsRead,
  dismissNotification,
  dismissAllNotifications,
} from '../../lib/api';

const POLL_INTERVAL_MS = 60000;
const MAX_BADGE_VALUE = 99;
const LAST_SEEN_STORAGE_KEY = 'crate.notifications.last_seen_at';
const CLOSE_DELAY_MS = 180;

const toTimestamp = (value) => {
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? timestamp : 0;
};

const readLastSeenAt = () => {
  try {
    return window.localStorage.getItem(LAST_SEEN_STORAGE_KEY) || '';
  } catch (error) {
    return '';
  }
};

const writeLastSeenAt = (value) => {
  try {
    window.localStorage.setItem(LAST_SEEN_STORAGE_KEY, value);
  } catch (error) {
    // Ignore storage errors.
  }
};

const formatNotificationDate = (value) => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

const getImageUrl = (notification) => (
  notification?.image?.sizes?.sm ||
  notification?.image?.url ||
  null
);

const getNotificationTimestamp = (notification) => (
  toTimestamp(notification?.published_at) || toTimestamp(notification?.created_at)
);

const resolveUnreadCount = (meta, notifications) => {
  const unreadFromMeta = Number(meta?.unread_count);
  if (Number.isFinite(unreadFromMeta) && unreadFromMeta >= 0) {
    return unreadFromMeta;
  }

  return notifications.filter((item) => !item.read_at).length;
};

const getLatestNotificationTimestamp = (notifications) => notifications.reduce((latest, item) => {
  const notificationTimestamp = getNotificationTimestamp(item);
  return notificationTimestamp > latest ? notificationTimestamp : latest;
}, 0);

const NotificationsBell = ({ onIndicatorChange }) => {
  const containerRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDismissingAll, setIsDismissingAll] = useState(false);
  const [activeNotificationId, setActiveNotificationId] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastSeenAt, setLastSeenAt] = useState(() => readLastSeenAt());

  const fetchNotifications = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setIsLoading(true);
      setErrorMessage('');
    }

    try {
      const response = await getNotifications();
      const loadedNotifications = Array.isArray(response?.data) ? response.data : [];
      setNotifications(loadedNotifications);
      setUnreadCount(resolveUnreadCount(response?.meta, loadedNotifications));
    } catch (error) {
      console.log('Could not load notifications', error);
      if (!silent) {
        setErrorMessage('Could not load notifications.');
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const intervalId = window.setInterval(() => {
      fetchNotifications({ silent: true });
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [fetchNotifications]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    clearCloseTimeout();
  }, [clearCloseTimeout]);

  const acknowledgeNotifications = useCallback((items) => {
    const notificationsToAcknowledge = Array.isArray(items) ? items : [];
    const latestTimestamp = getLatestNotificationTimestamp(notificationsToAcknowledge);
    const fallbackTimestamp = Date.now();
    const nextTimestamp = latestTimestamp || fallbackTimestamp;

    setLastSeenAt((previousValue) => {
      const previousTimestamp = toTimestamp(previousValue);
      const resolvedTimestamp = Math.max(previousTimestamp, nextTimestamp);
      const resolvedValue = new Date(resolvedTimestamp).toISOString();
      writeLastSeenAt(resolvedValue);
      return resolvedValue;
    });
  }, []);

  useEffect(() => {
    if (isOpen) {
      acknowledgeNotifications(notifications);
    }
  }, [isOpen, notifications, acknowledgeNotifications]);

  const openPanel = useCallback(() => {
    clearCloseTimeout();
    setIsOpen((previousOpen) => {
      if (!previousOpen) {
        acknowledgeNotifications(notifications);
        fetchNotifications({ silent: true });
      }
      return true;
    });
  }, [acknowledgeNotifications, clearCloseTimeout, fetchNotifications, notifications]);

  const closePanel = useCallback(() => {
    clearCloseTimeout();
    setIsOpen(false);
  }, [clearCloseTimeout]);

  const scheduleClosePanel = useCallback(() => {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      setIsOpen(false);
      closeTimeoutRef.current = null;
    }, CLOSE_DELAY_MS);
  }, [clearCloseTimeout]);

  const handleToggle = () => {
    if (isOpen) {
      closePanel();
      return;
    }
    openPanel();
  };

  const handleMouseEnter = () => {
    clearCloseTimeout();
    openPanel();
  };

  const handleMouseLeave = (event) => {
    if (containerRef.current && containerRef.current.contains(event.relatedTarget)) {
      return;
    }
    scheduleClosePanel();
  };

  const handleNotificationOpen = async (notification) => {
    if (!notification) {
      return;
    }

    const wasUnread = !notification.read_at;
    if (wasUnread) {
      setActiveNotificationId(notification.id);
      try {
        await markNotificationAsRead(notification.id);
        setNotifications((prev) => (
          prev.map((item) => (
            item.id === notification.id
              ? { ...item, read_at: new Date().toISOString() }
              : item
          ))
        ));
        setUnreadCount((prev) => Math.max(prev - 1, 0));
      } catch (error) {
        if (error?.response?.status === 404) {
          setNotifications((prev) => prev.filter((item) => item.id !== notification.id));
          if (wasUnread) {
            setUnreadCount((prev) => Math.max(prev - 1, 0));
          }
          fetchNotifications({ silent: true });
          return;
        }
        console.log('Could not mark notification as read', error);
      } finally {
        setActiveNotificationId(null);
      }
    }

    setIsOpen(false);
    if (notification.url) {
      window.location.href = notification.url;
    }
  };

  const handleDismissNotification = async (event, notificationId) => {
    event.preventDefault();
    event.stopPropagation();

    const targetNotification = notifications.find((item) => item.id === notificationId);
    setActiveNotificationId(notificationId);
    try {
      await dismissNotification(notificationId);
      setNotifications((prev) => prev.filter((item) => item.id !== notificationId));
      if (targetNotification && !targetNotification.read_at) {
        setUnreadCount((prev) => Math.max(prev - 1, 0));
      }
    } catch (error) {
      if (error?.response?.status === 404) {
        setNotifications((prev) => prev.filter((item) => item.id !== notificationId));
        if (targetNotification && !targetNotification.read_at) {
          setUnreadCount((prev) => Math.max(prev - 1, 0));
        }
        fetchNotifications({ silent: true });
        return;
      }
      console.log('Could not dismiss notification', error);
    } finally {
      setActiveNotificationId(null);
    }
  };

  const handleDismissAll = async () => {
    setIsDismissingAll(true);
    try {
      await dismissAllNotifications();
      setNotifications([]);
      setUnreadCount(0);
      fetchNotifications({ silent: true });
    } catch (error) {
      console.log('Could not dismiss all notifications', error);
    } finally {
      setIsDismissingAll(false);
    }
  };

  const hasUnread = unreadCount > 0;
  const lastSeenTimestamp = toTimestamp(lastSeenAt);
  const hasNewUnread = notifications.some((item) => (
    !item.read_at && getNotificationTimestamp(item) > lastSeenTimestamp
  ));
  const shouldShowBadge = notifications.length > 0
    ? hasNewUnread
    : (hasUnread && !lastSeenTimestamp);
  const badgeText = unreadCount > MAX_BADGE_VALUE ? `${MAX_BADGE_VALUE}+` : unreadCount;

  useEffect(() => {
    if (typeof onIndicatorChange !== 'function') {
      return;
    }

    onIndicatorChange({
      badgeText: hasUnread ? `${badgeText}` : '',
      hasUnread,
      shouldShowBadge,
      unreadCount,
    });
  }, [badgeText, hasUnread, onIndicatorChange, shouldShowBadge, unreadCount]);

  return (
    <div
      className={`notifications ${isOpen ? 'is-open' : ''}`}
      ref={containerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
    >
      <button
        type="button"
        className={`item notifications-menu-item ${hasUnread ? 'has-unread' : ''}`}
        onClick={handleToggle}
        aria-label={`Notifications${hasUnread ? ` (${unreadCount} unread)` : ''}`}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
      >
        <span className="notifications-row-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M12 2a6 6 0 0 0-6 6v2.6c0 .8-.2 1.5-.7 2.1L3.4 15c-.4.5-.5 1.2-.2 1.8.3.6.9 1 1.6 1H19.2c.7 0 1.3-.4 1.6-1 .3-.6.2-1.3-.2-1.8l-1.9-2.3c-.5-.6-.7-1.3-.7-2.1V8a6 6 0 0 0-6-6Zm0 20a2.9 2.9 0 0 0 2.6-1.6h-5.2A2.9 2.9 0 0 0 12 22Z" />
          </svg>
        </span>
        <span className="notifications-row-label">Notifications</span>
        {hasUnread &&
          <span className="notifications-badge">
            {badgeText}
          </span>
        }
        <span className="notifications-row-caret" aria-hidden="true">›</span>
      </button>
      <span className="notifications-hover-bridge" aria-hidden="true"></span>

      <div className={`notifications-panel ${isOpen ? 'is-open' : ''}`} role="dialog" aria-hidden={!isOpen}>
        <div className="notifications-panel-header">
          <div className="notifications-title">Notifications</div>
          <div className="notifications-actions">
            <button type="button" onClick={() => fetchNotifications()} disabled={isLoading}>
              Refresh
            </button>
            {notifications.length > 0 &&
              <button type="button" onClick={handleDismissAll} disabled={isDismissingAll}>
                {isDismissingAll ? 'Dismissing...' : 'Dismiss all'}
              </button>
            }
          </div>
        </div>

        {(isLoading && notifications.length === 0) &&
          <div className="notifications-state">
            Loading notifications...
          </div>
        }

        {(!isLoading && errorMessage) &&
          <div className="notifications-state error">
            {errorMessage}
          </div>
        }

        {(!isLoading && !errorMessage && notifications.length === 0) &&
          <div className="notifications-state">
            You have no notifications.
          </div>
        }

        {notifications.length > 0 &&
          <ul className="notifications-list">
            {notifications.map((notification) => {
              const isUnread = !notification.read_at;
              const isWorking = activeNotificationId === notification.id;
              const imageUrl = getImageUrl(notification);
              return (
                <li key={notification.id} className={`notification-item ${isUnread ? 'is-unread' : ''}`}>
                  <button
                    type="button"
                    className="notification-main"
                    onClick={() => handleNotificationOpen(notification)}
                    disabled={isWorking || isDismissingAll}
                  >
                    <div className={`notification-thumb ${imageUrl ? 'with-image' : ''}`}>
                      {imageUrl &&
                        <img src={imageUrl} alt="" loading="lazy" />
                      }
                      {!imageUrl &&
                        <span>{(notification?.type || '!').slice(0, 1).toUpperCase()}</span>
                      }
                    </div>

                    <div className="notification-content">
                      <div className="notification-title-row">
                        <span className="notification-title">{notification.title || 'Notification'}</span>
                        {isUnread && <span className="notification-pill">New</span>}
                      </div>
                      {notification.message &&
                        <p className="notification-message">{notification.message}</p>
                      }
                      <span className="notification-date">{formatNotificationDate(notification.published_at || notification.created_at)}</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    className="notification-dismiss"
                    onClick={(event) => handleDismissNotification(event, notification.id)}
                    disabled={isWorking || isDismissingAll}
                    aria-label="Dismiss notification"
                  >
                    &times;
                  </button>
                </li>
              );
            })}
          </ul>
        }
      </div>
    </div>
  );
};

export default NotificationsBell;
